/**
 * Cross-side recording state — populated by EITHER:
 *
 *   - Operator's `RecordingControl` start/stop/pause/resume mutations
 *     (write directly after the corresponding POST resolves).
 *   - Field's `FieldSystemHandler` `system/recording.started` /
 *     `recording.paused` / `recording.resumed` / `recording.stopped`
 *     envelope handlers (write on receipt from the operator's broadcast).
 *
 * `startedAt` is the operator's wall clock at the FIRST start of the
 * session (epoch ms). It's the anchor for the elapsed-time counter and
 * stays constant across pause/resume so the displayed clock reads the
 * total session duration. To show "live" recorded time excluding paused
 * intervals, subtract `pausedAccumulatedMs` from the elapsed delta.
 *
 * `paused` is the sub-state: true between a /pause and the matching
 * /resume. While paused, the REC pill flips to a "PAUSED" badge on both
 * sides. `pausedAt` marks the moment the current pause began (epoch ms);
 * when the operator resumes, we add `(resumeTime - pausedAt)` to
 * `pausedAccumulatedMs` and clear `pausedAt`.
 *
 * `ConsentHud` reads these to render the REC / PAUSED pill on both
 * sides — same UI primitive, AGENTS.md §4.
 */
import { create } from "zustand";

type Store = {
  /** True while the session is in EITHER recording or paused state.
   * Distinguishes "session has a recording lifecycle in flight" from
   * "no recording at all". */
  active: boolean;
  /** First /recording/start wall clock (epoch ms). Anchor for the
   * elapsed counter; stays constant across pause/resume cycles. */
  startedAt: number | null;
  /** Wall clock (epoch ms) the CURRENT segment began — set on start and
   * reset on each resume. Drives the min-segment-duration pause countdown
   * (the backend rejects a pause until the segment has run the minimum). */
  segmentStartedAt: number | null;
  /** True between /pause and /resume. */
  paused: boolean;
  /** Epoch ms of the current pause's start. Null when not paused. */
  pausedAt: number | null;
  /** Cumulative milliseconds spent in paused state across all cycles
   * in this session. Used to compute "live recorded time" = (now -
   * startedAt) - pausedAccumulatedMs - (paused ? now - pausedAt : 0). */
  pausedAccumulatedMs: number;
  set: (active: boolean, startedAt: number | null) => void;
  /** Mark the current cycle as paused. Idempotent if already paused. */
  pause: (at: number) => void;
  /** Resume the current cycle. Adds the paused interval to the cumulative
   * total. Idempotent if already not-paused. */
  resume: (at: number) => void;
  /** Hard reset — used by /stop. */
  reset: () => void;
};

export const useRecordingStore = create<Store>((set) => ({
  active: false,
  startedAt: null,
  segmentStartedAt: null,
  paused: false,
  pausedAt: null,
  pausedAccumulatedMs: 0,
  set: (active, startedAt) =>
    set({
      active,
      startedAt,
      segmentStartedAt: startedAt,
      paused: false,
      pausedAt: null,
      pausedAccumulatedMs: 0,
    }),
  pause: (at) =>
    set((s) =>
      s.paused ? s : { paused: true, pausedAt: at },
    ),
  resume: (at) =>
    set((s) => {
      if (!s.paused || s.pausedAt === null) return s;
      const delta = Math.max(0, at - s.pausedAt);
      return {
        paused: false,
        pausedAt: null,
        segmentStartedAt: at,
        pausedAccumulatedMs: s.pausedAccumulatedMs + delta,
      };
    }),
  reset: () =>
    set({
      active: false,
      startedAt: null,
      segmentStartedAt: null,
      paused: false,
      pausedAt: null,
      pausedAccumulatedMs: 0,
    }),
}));
